// src/components/CustomerRequestJob.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  AddCircle as AddIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, getDocs } from "firebase/firestore";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

function CustomerRequestJob({ onSuccess }) {
  const { customer } = useCustomerAuth();
  const [jobTemplates, setJobTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    serviceType: "",
    vehicleReg: "",
    vehicleMake: "",
    vehicleModel: "",
    location: customer?.address || "",
    preferredDate: "",
    preferredTime: "anytime",
    description: "",
    isUrgent: false,
  });

  // Fetch job templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templatesSnapshot = await getDocs(collection(db, "jobTemplates"));
        const templates = templatesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setJobTemplates(templates);
      } catch (err) {
        console.error("Error fetching templates:", err);
      }
    };
    fetchTemplates();
  }, []);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      // Validate required fields
      if (!formData.serviceType || !formData.vehicleReg || !formData.location) {
        throw new Error("Please fill in all required fields");
      }

      // Create job request
      await addDoc(collection(db, "jobRequests"), {
        customerId: customer.id,
        customerName: customer.name,
        customerEmail: customer.email,
        customerPhone: customer.phone,
        serviceType: formData.serviceType,
        vehicleRegistration: formData.vehicleReg,
        vehicleMake: formData.vehicleMake,
        vehicleModel: formData.vehicleModel,
        location: formData.location,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        description: formData.description,
        isUrgent: formData.isUrgent,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      setMessage({ type: "success", text: "Job request submitted successfully! We'll contact you within 24 hours." });
      
      // Reset form
      setFormData({
        serviceType: "",
        vehicleReg: "",
        vehicleMake: "",
        vehicleModel: "",
        location: customer?.address || "",
        preferredDate: "",
        preferredTime: "anytime",
        description: "",
        isUrgent: false,
      });

      if (onSuccess) {
        setTimeout(() => onSuccess(), 2000);
      }
    } catch (err) {
      console.error("Error submitting request:", err);
      setMessage({ type: "error", text: err.message || "Failed to submit request. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: "white", fontWeight: 700, mb: 1 }}>
          Request a Job
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Tell us what you need and we'll get back to you
        </Typography>
      </Box>

      {/* Success/Error Message */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Service Type */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  What do you need? *
                </Typography>
                <TextField
                  select
                  fullWidth
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <MenuItem value="">Select a service</MenuItem>
                  {jobTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.name}>
                      {template.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="other">Other (specify in description)</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Vehicle Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Vehicle Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Vehicle Registration *
                </Typography>
                <TextField
                  fullWidth
                  name="vehicleReg"
                  value={formData.vehicleReg}
                  onChange={handleChange}
                  placeholder="AB12 CDE"
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Make
                </Typography>
                <TextField
                  fullWidth
                  name="vehicleMake"
                  value={formData.vehicleMake}
                  onChange={handleChange}
                  placeholder="e.g. Volvo"
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Model
                </Typography>
                <TextField
                  fullWidth
                  name="vehicleModel"
                  value={formData.vehicleModel}
                  onChange={handleChange}
                  placeholder="e.g. FH"
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Location & Timing */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  When & Where
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Location *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter the service location"
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Preferred Date
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Preferred Time
                </Typography>
                <TextField
                  select
                  fullWidth
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <MenuItem value="anytime">Anytime</MenuItem>
                  <MenuItem value="morning">Morning (9AM - 12PM)</MenuItem>
                  <MenuItem value="afternoon">Afternoon (12PM - 5PM)</MenuItem>
                  <MenuItem value="evening">Evening (5PM - 8PM)</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Additional Details */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Additional Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Description / Special Requirements
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe what you need or any special requirements..."
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isUrgent"
                      checked={formData.isUrgent}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  }
                  label="This is urgent"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{
                      background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
                      },
                    }}
                  >
                    {loading ? "Submitting..." : "Submit Request"}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card sx={{ mt: 3, bgcolor: "rgba(59,130,246,0.05)" }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            📋 What happens next?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            1. We'll review your request within 24 hours<br />
            2. You'll receive a confirmation email or phone call<br />
            3. We'll schedule the job at your preferred time<br />
            4. You'll be able to track progress in "My Jobs"
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default CustomerRequestJob;
